import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect } from 'react';
import {
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $createParagraphNode,
  $isRangeSelection,
} from 'lexical';

export default function ImageDragDropPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const rootElem = editor.getRootElement();
    if (!rootElem) return;

    const onDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const wrapper = target.closest('[data-node-key]');
      if (!wrapper) return;

      const nodeKey = wrapper.getAttribute('data-node-key');
      if (!nodeKey) return;

      e.dataTransfer?.setData('application/x-lexical-node', nodeKey);
      e.dataTransfer!.effectAllowed = 'move';
    };

    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('application/x-lexical-node')) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }
    };

    const onDrop = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('application/x-lexical-node')) return;

      e.preventDefault();

      const nodeKey = e.dataTransfer.getData('application/x-lexical-node');
      if (!nodeKey) return;

      const caretRange = document.caretRangeFromPoint?.(e.clientX, e.clientY);

      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (!node) return;

        node.remove();

        const selection = $getSelection();

        if (caretRange && $isRangeSelection(selection)) {
          selection.applyDOMRange(caretRange);
          selection.insertNodes([node]);
          return;
        }

        const root = $getRoot();
        const last = root.getLastChild();

        if (last) {
          last.insertAfter(node);
        } else {
          const paragraph = $createParagraphNode();
          paragraph.append(node);
          root.append(paragraph);
        }
      });
    };

    rootElem.addEventListener('dragstart', onDragStart);
    rootElem.addEventListener('dragover', onDragOver);
    rootElem.addEventListener('drop', onDrop);

    return () => {
      rootElem.removeEventListener('dragstart', onDragStart);
      rootElem.removeEventListener('dragover', onDragOver);
      rootElem.removeEventListener('drop', onDrop);
    };
  }, [editor]);

  return null;
}
